<template>
  <div class="staff-management-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-account-group</v-icon>
          </div>
          <div>
            <div class="page-title">STAFF DIRECTORY</div>
            <div class="page-subtitle">Manage store staff accounts, access roles, security credentials &amp; salesman codes</div>
          </div>
        </div>

        <!-- Right: Action Bar (Search + Refresh + Quick Shortcut) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search by name, code, email, phone..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            icon
            small
            title="Refresh Staff List"
            class="refresh-icon-btn"
            @click="getStaff(); getRoless()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#8d7c61"
            class="btn-secondary text-capitalize font-weight-bold"
            @click="$router.push('/rolesList')"
          >
            <v-icon left small color="#8d7c61">mdi-shield-account-outline</v-icon>
            Roles &amp; Permissions
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1 font-weight-bold"
            @click="openAddStaffModal()"
          >
            <v-icon left small>mdi-account-plus</v-icon>
            Add Staff
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card" @click="selectedFilter = 'All'" :class="{ 'kpi-card-active': selectedFilter === 'All' }">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL STAFF</div>
          <div class="kpi-value">{{ totalStaffCount }}</div>
          <div class="kpi-hint">Registered staff members</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-account-group-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card" @click="selectedFilter = 'Active'" :class="{ 'kpi-card-active': selectedFilter === 'Active' }">
        <div class="kpi-content">
          <div class="kpi-label">ACTIVE MEMBERS</div>
          <div class="kpi-value">{{ activeStaffCount }}</div>
          <div class="kpi-hint">Ready for billing &amp; operations</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-account-check-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card" @click="selectedFilter = 'Inactive'" :class="{ 'kpi-card-active': selectedFilter === 'Inactive' }">
        <div class="kpi-content">
          <div class="kpi-label">INACTIVE / ON LEAVE</div>
          <div class="kpi-value">{{ inactiveStaffCount }}</div>
          <div class="kpi-hint">Temporarily disabled accounts</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fef2f2; color: #dc2626;">
          <v-icon size="20" color="#dc2626">mdi-account-clock-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card" @click="$router.push('/rolesList')">
        <div class="kpi-content">
          <div class="kpi-label">ROLES CONFIGURED</div>
          <div class="kpi-value">{{ rolesCount }}</div>
          <div class="kpi-hint">Custom access permission sets</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-shield-lock-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Status Tabs Filter Bar -->
    <div class="filter-tab-bar mb-3 d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
      <div class="d-flex align-center" style="gap: 6px;">
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': selectedFilter === 'All' }"
          @click="selectedFilter = 'All'"
        >
          All Staff
          <span class="filter-pill-badge">{{ totalStaffCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': selectedFilter === 'Active' }"
          @click="selectedFilter = 'Active'"
        >
          Active
          <span class="filter-pill-badge active-pill-badge">{{ activeStaffCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': selectedFilter === 'Inactive' }"
          @click="selectedFilter = 'Inactive'"
        >
          Inactive
          <span class="filter-pill-badge inactive-pill-badge">{{ inactiveStaffCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': selectedFilter === 'Expire' }"
          @click="selectedFilter = 'Expire'"
        >
          Expired
          <span class="filter-pill-badge expired-pill-badge">{{ expiredStaffCount }}</span>
        </button>
      </div>

      <div class="table-total-count text-muted" style="font-size: 12.5px;">
        Showing <span class="font-weight-bold text-slate-800">{{ filteredStaffList.length }}</span> records
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <div v-if="filteredStaffList && filteredStaffList.length > 0">
        <div class="table-responsive-wrapper">
          <table class="pro-staff-table">
            <thead>
              <tr>
                <th style="width: 55px;" class="text-center">#</th>
                <th style="min-width: 220px;" class="text-left">Staff Member</th>
                <th style="width: 120px;" class="text-center">Staff Code</th>
                <th style="width: 160px;" class="text-left">Role</th>
                <th style="min-width: 200px;" class="text-left">Contact Info</th>
                <th style="width: 120px;" class="text-center">Status</th>
                <th style="width: 110px;" class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in filteredStaffList" :key="value._id || i" class="table-row">
                <!-- S.No -->
                <td class="text-center text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>

                <!-- Staff Member (Avatar + Full Name + Username Handle) -->
                <td>
                  <div class="d-flex align-center py-1">
                    <div class="staff-avatar mr-3" :style="{ background: getAvatarBg(value.firstname) }">
                      {{ getInitials(value.firstname, value.lastname) }}
                    </div>
                    <div>
                      <div class="staff-name-text">
                        {{ getFullName(value.firstname, value.lastname) }}
                      </div>
                      <div class="staff-handle-text" v-if="value.username">
                        @{{ value.username }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Staff Code -->
                <td class="text-center">
                  <span class="staff-code-pill" v-if="value.staffCode">
                    #{{ value.staffCode }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- Role -->
                <td>
                  <span class="role-chip" v-if="value.subRoleId && value.subRoleId.roles">
                    <v-icon x-small color="#8c6227" class="mr-1">mdi-shield-check-outline</v-icon>
                    {{ value.subRoleId.roles }}
                  </span>
                  <span v-else class="text-muted" style="font-size: 12px;">Staff</span>
                </td>

                <!-- Contact Info (Phone + Email) -->
                <td>
                  <div class="d-flex flex-column py-1" style="gap: 3px;">
                    <div class="contact-line" v-if="value.phone">
                      <v-icon x-small color="#94a3b8" class="mr-1">mdi-phone-outline</v-icon>
                      <a :href="'tel:' + value.phone" class="contact-link">{{ value.phone }}</a>
                    </div>
                    <div class="contact-line" v-if="value.email">
                      <v-icon x-small color="#94a3b8" class="mr-1">mdi-email-outline</v-icon>
                      <a :href="'mailto:' + value.email" class="contact-link text-truncate" style="max-width: 180px;">{{ value.email }}</a>
                    </div>
                  </div>
                </td>

                <!-- Status (Interactive Badge) -->
                <td class="text-center">
                  <!-- Active Status -->
                  <button
                    v-if="value.staffStatus === 'Active'"
                    class="status-btn status-active"
                    title="Click to deactivate this staff member"
                    @click="promptDeactivate(value)"
                  >
                    <span class="status-pulse-dot green-dot"></span>
                    Active
                  </button>

                  <!-- Inactive Status -->
                  <button
                    v-else-if="value.staffStatus === 'Inactive'"
                    class="status-btn status-inactive"
                    title="Click to reactivate this staff member"
                    @click="promptActivate(value)"
                  >
                    <span class="status-pulse-dot red-dot"></span>
                    Inactive
                  </button>

                  <!-- Expired Status -->
                  <span
                    v-else-if="value.staffStatus === 'Expire'"
                    class="status-btn status-expired"
                    title="Account expired"
                  >
                    <span class="status-pulse-dot grey-dot"></span>
                    Expired
                  </span>

                  <span v-else class="status-btn status-active">
                    <span class="status-pulse-dot green-dot"></span>
                    Active
                  </span>
                </td>

                <!-- Actions (Edit & Delete/Expire) -->
                <td class="text-center">
                  <div class="d-flex align-center justify-center" style="gap: 6px;">
                    <!-- Edit Action -->
                    <v-btn
                      icon
                      small
                      class="row-action-btn edit-btn"
                      title="Edit Staff Details"
                      :disabled="value.staffStatus === 'Expire'"
                      @click="openEditModal(value)"
                    >
                      <v-icon small color="#9a6b27">mdi-pencil-outline</v-icon>
                    </v-btn>

                    <!-- Delete / Expire Action -->
                    <v-btn
                      icon
                      small
                      class="row-action-btn delete-btn"
                      title="Deactivate or Remove Staff"
                      :disabled="value.staffStatus === 'Expire'"
                      @click="openDeleteModal(value)"
                    >
                      <v-icon small color="#ef4444">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Custom Pagination & Summary Bar -->
        <div class="table-pagination-bar px-4 py-3 d-flex flex-wrap align-center justify-space-between">
          <div class="pagination-info">
            Showing
            <span class="font-weight-bold text-slate-800">{{ showingFrom }}</span>
            to
            <span class="font-weight-bold text-slate-800">{{ showingTo }}</span>
            of
            <span class="font-weight-bold text-slate-800">{{ totalStaffCount }}</span>
            staff members
          </div>

          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            circle
            color="#9a6b27"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-account-search-outline</v-icon>
        </div>
        <div class="empty-title">No Staff Members Found</div>
        <div class="empty-subtitle">
          {{ keyword ? 'No staff records matched your search query. Try clearing filters.' : 'There are currently no staff members matching the selected criteria.' }}
        </div>
        <div class="mt-4 d-flex justify-center" style="gap: 10px;">
          <v-btn v-if="keyword || selectedFilter !== 'All'" small outlined color="#0f172a" @click="clearFilters()">
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            Clear Filters
          </v-btn>
          <v-btn small dark color="#9a6b27" @click="openAddStaffModal()">
            <v-icon small left>mdi-account-plus</v-icon>
            Add Staff Member
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- ============================================== -->
    <!-- MODAL 1: ADD STAFF MEMBER                      -->
    <!-- ============================================== -->
    <v-dialog v-model="addStaff" persistent max-width="680px">
      <v-card class="modal-card pa-6">
        <!-- Header -->
        <div class="d-flex align-center justify-space-between mb-5">
          <div class="d-flex align-center">
            <div class="modal-icon-circle mr-3">
              <v-icon color="#9a6b27" size="22">mdi-account-plus</v-icon>
            </div>
            <div>
              <h2 class="modal-title">Add New Staff Member</h2>
              <p class="modal-subtitle">Create login credentials, sales code and assign role permissions</p>
            </div>
          </div>
          <v-btn icon small @click="closeAddStaffModal()" class="modal-close-btn">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-5"></v-divider>

        <!-- Form -->
        <v-form ref="addStaffForm" v-model="addcat" @submit.prevent="validate()">
          <v-layout wrap>
            <!-- First Name -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">FIRST NAME <span class="required-star">*</span></label>
              <v-text-field
                v-model="firstname"
                placeholder="e.g. John"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Last Name -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">LAST NAME</label>
              <v-text-field
                v-model="lastname"
                placeholder="e.g. Doe"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Staff Code -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">STAFF CODE <span class="required-star">*</span></label>
              <v-text-field
                v-model="staffCode"
                placeholder="e.g. SC01, ALB"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                class="form-text-field code-input"
              ></v-text-field>
              <span class="field-hint">Used to identify salesman on billing &amp; estimates</span>
            </v-flex>

            <!-- Role Selector with Inline Add Role -->
            <v-flex xs12 sm6 px-2 pb-3>
              <div class="d-flex align-center justify-space-between mb-1">
                <label class="form-input-label mb-0">ROLE <span class="required-star">*</span></label>
                <button type="button" class="inline-add-role-btn" @click="addRole = true">
                  <v-icon x-small color="#9a6b27" class="mr-1">mdi-plus</v-icon>
                  New Role
                </button>
              </div>
              <v-select
                v-model="roleId"
                :items="rolesList"
                item-text="roles"
                item-value="_id"
                placeholder="Select Assigned Role"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                class="form-text-field"
              ></v-select>
            </v-flex>

            <!-- Phone Number -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">PHONE NUMBER <span class="required-star">*</span></label>
              <v-text-field
                v-model="phone"
                type="number"
                placeholder="10-digit mobile number"
                outlined
                dense
                color="#9a6b27"
                :rules="phoneRules"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Email Address -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">EMAIL ADDRESS <span class="required-star">*</span></label>
              <v-text-field
                v-model="email"
                type="email"
                placeholder="name@jewelbiz.com"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required, rules.email]"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider class="my-4"></v-divider>

          <!-- Footer Actions -->
          <div class="d-flex align-center justify-end" style="gap: 12px;">
            <v-btn
              outlined
              color="#64748b"
              height="38px"
              class="modal-btn-cancel font-weight-bold"
              @click="closeAddStaffModal()"
            >
              Cancel
            </v-btn>
            <v-btn
              dark
              color="#9a6b27"
              height="38px"
              class="modal-btn-primary font-weight-bold elevation-1"
              @click="validate()"
            >
              <v-icon small left>mdi-check</v-icon>
              Create Staff Member
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 2: EDIT STAFF MEMBER                     -->
    <!-- ============================================== -->
    <v-dialog v-model="editdialogue" persistent max-width="640px">
      <v-card class="modal-card pa-6">
        <!-- Header -->
        <div class="d-flex align-center justify-space-between mb-5">
          <div class="d-flex align-center">
            <div class="modal-icon-circle mr-3">
              <v-icon color="#9a6b27" size="22">mdi-account-edit-outline</v-icon>
            </div>
            <div>
              <h2 class="modal-title">Edit Staff Details</h2>
              <p class="modal-subtitle">Update staff profile, contact info and role permissions</p>
            </div>
          </div>
          <v-btn icon small @click="editdialogue = false" class="modal-close-btn">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-5"></v-divider>

        <!-- Form -->
        <v-form ref="editStaffForm" v-model="addcat2" @submit.prevent="editValidate()">
          <v-layout wrap>
            <!-- First Name -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">FIRST NAME <span class="required-star">*</span></label>
              <v-text-field
                v-model="curItem.firstname"
                placeholder="First Name"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Last Name -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">LAST NAME</label>
              <v-text-field
                v-model="curItem.lastname"
                placeholder="Last Name"
                outlined
                dense
                color="#9a6b27"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Assigned Role -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">ROLE <span class="required-star">*</span></label>
              <v-select
                v-model="curItem.subRoleId"
                :items="rolesList"
                item-text="roles"
                item-value="_id"
                placeholder="Role"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required]"
                hide-details="auto"
                class="form-text-field"
              ></v-select>
            </v-flex>

            <!-- Phone Number -->
            <v-flex xs12 sm6 px-2 pb-3>
              <label class="form-input-label">PHONE NUMBER <span class="required-star">*</span></label>
              <v-text-field
                v-model="curItem.phone"
                type="number"
                placeholder="Phone"
                outlined
                dense
                color="#9a6b27"
                :rules="phoneRules"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>

            <!-- Email Address -->
            <v-flex xs12 px-2 pb-3>
              <label class="form-input-label">EMAIL ADDRESS <span class="required-star">*</span></label>
              <v-text-field
                v-model="curItem.email"
                type="email"
                placeholder="Email"
                outlined
                dense
                color="#9a6b27"
                :rules="[rules.required, rules.email]"
                hide-details="auto"
                class="form-text-field"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider class="my-4"></v-divider>

          <!-- Actions -->
          <div class="d-flex align-center justify-end" style="gap: 12px;">
            <v-btn
              outlined
              color="#64748b"
              height="38px"
              class="modal-btn-cancel font-weight-bold"
              @click="editdialogue = false"
            >
              Cancel
            </v-btn>
            <v-btn
              dark
              color="#9a6b27"
              height="38px"
              class="modal-btn-primary font-weight-bold elevation-1"
              @click="editValidate()"
            >
              <v-icon small left>mdi-check</v-icon>
              Save Changes
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 3: ADD ROLE                              -->
    <!-- ============================================== -->
    <v-dialog v-model="addRole" persistent max-width="440px">
      <v-card class="modal-card pa-6">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <div class="modal-icon-circle mr-3">
              <v-icon color="#9a6b27" size="20">mdi-shield-plus-outline</v-icon>
            </div>
            <div>
              <h2 class="modal-title">Create New Role</h2>
              <p class="modal-subtitle">Add a role category for staff members</p>
            </div>
          </div>
          <v-btn icon small @click="addRole = false" class="modal-close-btn">
            <v-icon small color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div class="mb-4">
          <label class="form-input-label">ROLE NAME <span class="required-star">*</span></label>
          <v-text-field
            v-model="roles"
            placeholder="e.g. Senior Cashier, Floor Manager"
            outlined
            dense
            color="#9a6b27"
            hide-details="auto"
            class="form-text-field"
          ></v-text-field>
        </div>

        <div class="d-flex align-center justify-end" style="gap: 10px;">
          <v-btn outlined color="#64748b" height="36px" class="modal-btn-cancel font-weight-bold" @click="addRole = false">
            Cancel
          </v-btn>
          <v-btn dark color="#9a6b27" height="36px" class="modal-btn-primary font-weight-bold elevation-1" @click="AddRole()">
            Save Role
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 4: DEACTIVATE CONFIRMATION               -->
    <!-- ============================================== -->
    <v-dialog v-model="changestatus" persistent max-width="420px">
      <v-card class="modal-card pa-6 text-center">
        <div class="confirm-icon-box warning-box mb-3">
          <v-icon size="28" color="#f59e0b">mdi-alert-circle-outline</v-icon>
        </div>
        <h2 class="confirm-title">Deactivate Staff Member?</h2>
        <p class="confirm-desc">
          Are you sure you want to deactivate <span class="font-weight-bold text-slate-800">{{ activeStaffTargetName }}</span>?
          They will temporarily be prevented from signing into the portal.
        </p>
        <div class="d-flex align-center justify-center mt-4" style="gap: 12px;">
          <v-btn outlined color="#64748b" height="36px" class="modal-btn-cancel font-weight-bold" @click="changestatus = false">
            Cancel
          </v-btn>
          <v-btn dark color="#dc2626" height="36px" class="font-weight-bold elevation-1" style="border-radius: 8px;" @click="changestaffstatus()">
            Yes, Deactivate
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 5: REACTIVATE CONFIRMATION               -->
    <!-- ============================================== -->
    <v-dialog v-model="changestatus2" persistent max-width="420px">
      <v-card class="modal-card pa-6 text-center">
        <div class="confirm-icon-box success-box mb-3">
          <v-icon size="28" color="#16a34a">mdi-check-circle-outline</v-icon>
        </div>
        <h2 class="confirm-title">Reactivate Staff Member?</h2>
        <p class="confirm-desc">
          Reactivate <span class="font-weight-bold text-slate-800">{{ activeStaffTargetName }}</span> to restore full dashboard access and billing privileges.
        </p>
        <div class="d-flex align-center justify-center mt-4" style="gap: 12px;">
          <v-btn outlined color="#64748b" height="36px" class="modal-btn-cancel font-weight-bold" @click="changestatus2 = false">
            Cancel
          </v-btn>
          <v-btn dark color="#16a34a" height="36px" class="font-weight-bold elevation-1" style="border-radius: 8px;" @click="changestaffstatus2()">
            Yes, Activate
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ============================================== -->
    <!-- MODAL 6: DELETE / EXPIRE CONFIRMATION          -->
    <!-- ============================================== -->
    <v-dialog v-model="deletedialog" persistent max-width="460px">
      <v-card class="modal-card pa-6 text-center">
        <div class="confirm-icon-box danger-box mb-3">
          <v-icon size="28" color="#dc2626">mdi-account-remove-outline</v-icon>
        </div>
        <h2 class="confirm-title">Remove Staff Member</h2>
        <p class="confirm-desc">
          Choose whether to expire this staff account (retains historical sales/audit logs) or delete permanently.
        </p>
        <div class="d-flex flex-column" style="gap: 8px; margin-top: 18px;">
          <v-btn
            dark
            color="#475569"
            height="40px"
            class="font-weight-bold elevation-1"
            style="border-radius: 8px; text-transform: none;"
            @click="expireStaff()"
          >
            <v-icon small left>mdi-account-clock-outline</v-icon>
            Mark Account as Expired (Recommended)
          </v-btn>
          <v-btn
            dark
            color="#dc2626"
            height="40px"
            class="font-weight-bold elevation-1"
            style="border-radius: 8px; text-transform: none;"
            @click="deleteStaff()"
          >
            <v-icon small left>mdi-delete-forever-outline</v-icon>
            Delete Account Permanently
          </v-btn>
          <v-btn
            text
            color="#64748b"
            height="36px"
            class="mt-1 font-weight-bold"
            @click="deletedialog = false"
          >
            Cancel
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "StaffManagement",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      addcat: false,
      addcat2: false,
      page: 1,
      Pagelength: 1,
      statusId: null,
      staffstatus: "Active",
      addRole: false,
      roles: null,
      jewelleryId: null,
      rolesList: [],
      firstname: null,
      lastname: null,
      staffCode: null,
      roleId: null,
      phone: null,
      email: null,
      addStaff: false,
      staffList: [],
      curItem: {},
      curId: null,
      editdialogue: false,
      deletedialog: false,
      changestatus: false,
      changestatus2: false,
      keyword: null,
      selectedFilter: "All",
      activeStaffTargetName: "",
      rules: {
        required: (value) => !!value || "Required.",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail address.";
        },
      },
      phoneRules: [
        (v) => !!v || "Phone is required",
        (v) => (v && v.length === 10) || "Phone number must be exactly 10 digits",
        (v) => /^\d{10}$/.test(v) || "Phone number must contain numbers only",
      ],
    };
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getStaff();
    },
    page() {
      this.getStaff();
    },
  },
  mounted() {
    this.getRoless();
    this.getStaff();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    filteredStaffList() {
      if (!this.staffList) return [];
      if (this.selectedFilter === "All") return this.staffList;
      return this.staffList.filter(
        (item) => item.staffStatus === this.selectedFilter
      );
    },
    totalStaffCount() {
      return this.staffList ? this.staffList.length : 0;
    },
    activeStaffCount() {
      if (!this.staffList) return 0;
      return this.staffList.filter((s) => s.staffStatus === "Active").length;
    },
    inactiveStaffCount() {
      if (!this.staffList) return 0;
      return this.staffList.filter((s) => s.staffStatus === "Inactive").length;
    },
    expiredStaffCount() {
      if (!this.staffList) return 0;
      return this.staffList.filter((s) => s.staffStatus === "Expire").length;
    },
    rolesCount() {
      return this.rolesList ? this.rolesList.length : 0;
    },
    showingFrom() {
      if (!this.filteredStaffList || this.filteredStaffList.length === 0) return 0;
      return (this.page - 1) * 20 + 1;
    },
    showingTo() {
      if (!this.filteredStaffList || this.filteredStaffList.length === 0) return 0;
      return (this.page - 1) * 20 + this.filteredStaffList.length;
    },
  },
  methods: {
    getInitials(first, last) {
      const f = (first || "").trim().charAt(0).toUpperCase();
      const l = (last || "").trim().charAt(0).toUpperCase();
      return (f + l) || "ST";
    },
    getFullName(first, last) {
      const parts = [first, last].filter(Boolean);
      return parts.join(" ") || "Unnamed Staff";
    },
    getAvatarBg(seed) {
      const colors = [
        "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
        "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
        "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
        "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)",
        "linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)",
      ];
      if (!seed) return colors[0];
      const charCode = seed.charCodeAt(0) || 0;
      return colors[charCode % colors.length];
    },
    clearFilters() {
      this.keyword = "";
      this.selectedFilter = "All";
      this.getStaff();
    },
    openAddStaffModal() {
      this.resetForm();
      this.addStaff = true;
    },
    closeAddStaffModal() {
      this.addStaff = false;
      this.resetForm();
    },
    openEditModal(item) {
      this.curItem = {
        _id: item._id,
        firstname: item.firstname || "",
        lastname: item.lastname || "",
        subRoleId: item.subRoleId ? item.subRoleId._id : null,
        phone: item.phone || "",
        email: item.email || "",
      };
      this.editdialogue = true;
    },
    openDeleteModal(item) {
      this.curId = item._id;
      this.deletedialog = true;
    },
    promptDeactivate(item) {
      this.statusId = item._id;
      this.activeStaffTargetName = this.getFullName(item.firstname, item.lastname);
      this.changestatus = true;
    },
    promptActivate(item) {
      this.statusId = item._id;
      this.activeStaffTargetName = this.getFullName(item.firstname, item.lastname);
      this.changestatus2 = true;
    },
    resetForm() {
      this.firstname = null;
      this.lastname = null;
      this.staffCode = null;
      this.roleId = null;
      this.phone = null;
      this.email = null;
      if (this.$refs.addStaffForm) {
        this.$refs.addStaffForm.resetValidation();
      }
    },
    validate() {
      if (!this.firstname) {
        this.msg = "Please enter first name";
        this.showsnackbar = true;
        return;
      } else if (!this.staffCode) {
        this.msg = "Please enter staff code";
        this.showsnackbar = true;
        return;
      } else if (!this.roleId) {
        this.msg = "Please choose a role";
        this.showsnackbar = true;
        return;
      } else if (!this.phone) {
        this.msg = "Please enter phone number";
        this.showsnackbar = true;
        return;
      } else if (!this.email) {
        this.msg = "Please enter email address";
        this.showsnackbar = true;
        return;
      } else {
        this.AddStaff();
      }
    },
    editValidate() {
      if (!this.curItem.firstname) {
        this.msg = "Please enter first name";
        this.showsnackbar = true;
        return;
      } else if (!this.curItem.subRoleId) {
        this.msg = "Please choose a role";
        this.showsnackbar = true;
        return;
      } else if (!this.curItem.phone) {
        this.msg = "Please enter phone number";
        this.showsnackbar = true;
        return;
      } else if (!this.curItem.email) {
        this.msg = "Please enter email address";
        this.showsnackbar = true;
        return;
      } else {
        this.editStaff();
      }
    },
    getStaff() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/user/all/staff",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          page: this.page,
          count: 20,
          keyword: this.keyword,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.staffList = response.data.data || [];
            this.Pagelength = Math.ceil((response.data.count || this.staffList.length) / 20) || 1;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg || "Failed to load staff list";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    getRoless() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/getalllist/roles",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.appUser ? this.appUser._id : null,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.rolesList = response.data.data || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    AddRole() {
      if (!this.roles) {
        this.msg = "Please enter role name";
        this.showsnackbar = true;
        return;
      }
      axios({
        url: "/add/roles",
        method: "POST",
        data: {
          roles: this.roles,
          jewelleryId: this.jewelleryId,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Role created successfully";
            this.showsnackbar = true;
            this.addRole = false;
            this.jewelleryId = null;
            this.roles = null;
            this.getRoless();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    AddStaff() {
      this.appLoading = true;
      axios({
        url: "/user/add",
        method: "POST",
        data: {
          roleId: this.roleId,
          firstname: this.firstname,
          lastname: this.lastname,
          email: this.email,
          phone: this.phone,
          staffCode: this.staffCode,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff member added successfully";
            this.showsnackbar = true;
            this.addStaff = false;
            this.resetForm();
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    editStaff() {
      this.appLoading = true;
      axios({
        url: "/user/adminedit",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curItem._id,
          subRoleId: this.curItem.subRoleId,
          firstname: this.curItem.firstname,
          lastname: this.curItem.lastname,
          email: this.curItem.email,
          phone: this.curItem.phone,
        },
      })
        .then((response) => {
          this.editdialogue = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff updated successfully";
            this.showsnackbar = true;
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    deleteStaff() {
      this.appLoading = true;
      axios({
        url: "/user/remove",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff member removed";
            this.showsnackbar = true;
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    expireStaff() {
      this.appLoading = true;
      axios({
        url: "/user/expire",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff account marked as expired";
            this.showsnackbar = true;
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    changestaffstatus() {
      this.appLoading = true;
      axios({
        url: "/change/status",
        method: "POST",
        data: {
          id: this.statusId,
          status: "Inactive",
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff member deactivated";
            this.showsnackbar = true;
            this.changestatus = false;
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    changestaffstatus2() {
      this.appLoading = true;
      axios({
        url: "/change/status",
        method: "POST",
        data: {
          id: this.statusId,
          status: "Active",
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Staff member reactivated";
            this.showsnackbar = true;
            this.changestatus2 = false;
            this.getStaff();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');

.staff-management-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1e293b;
}

/* 1. Top Executive Action Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

.search-input-wrap {
  width: 290px;
}

@media (max-width: 600px) {
  .search-input-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot) {
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  min-height: 38px !important;
  font-size: 13px !important;
}

.refresh-icon-btn {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  width: 38px !important;
  height: 38px !important;
}

.btn-secondary {
  border-radius: 8px !important;
  font-size: 12.5px !important;
  height: 38px !important;
  border-color: #d4af37 !important;
  color: #9a6b27 !important;
  background: #fffdfa !important;
}

.btn-primary {
  border-radius: 8px !important;
  font-size: 12.5px !important;
  height: 38px !important;
  box-shadow: 0 2px 6px rgba(154, 106, 44, 0.25) !important;
}

/* 2. 4 KPI Overview Metric Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 960px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 550px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  cursor: pointer;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.kpi-card-active {
  border-color: #9a6b27 !important;
  background: #fffdf8 !important;
  box-shadow: 0 2px 8px rgba(154, 106, 44, 0.12) !important;
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  margin: 4px 0 2px 0;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 3. Filter Pill Bar */
.filter-pill {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-pill:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #cbd5e1;
}

.filter-pill-active {
  background: #0f172a !important;
  color: #ffffff !important;
  border-color: #0f172a !important;
}

.filter-pill-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.filter-pill-active .filter-pill-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.active-pill-badge {
  background: #dcfce7;
  color: #16a34a;
}

.inactive-pill-badge {
  background: #fee2e2;
  color: #dc2626;
}

.expired-pill-badge {
  background: #f1f5f9;
  color: #64748b;
}

/* 4. Table Styles */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
}

.pro-staff-table {
  width: 100%;
  border-collapse: collapse;
}

.pro-staff-table th {
  background-color: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.pro-staff-table td {
  font-size: 13px;
  color: #334155;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #fafbfd;
}

.staff-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.staff-name-text {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
}

.staff-handle-text {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 1px;
}

.staff-code-pill {
  font-family: monospace;
  font-size: 11.5px;
  font-weight: 700;
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.5px;
  display: inline-block;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #713f12;
  background: #fefce8;
  border: 1px solid #fef08a;
  padding: 3px 9px;
  border-radius: 6px;
}

.contact-line {
  font-size: 12px;
  color: #475569;
  display: flex;
  align-items: center;
}

.contact-link {
  color: #475569;
  text-decoration: none;
  font-weight: 500;
}

.contact-link:hover {
  color: #9a6b27;
  text-decoration: underline;
}

/* Status Pill Badges */
.status-btn {
  border: none;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.status-active {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-active:hover {
  background: #bbf7d0;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.2);
}

.status-inactive {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.status-inactive:hover {
  background: #fecaca;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.2);
}

.status-expired {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  cursor: default;
}

.status-pulse-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.green-dot {
  background: #16a34a;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.2);
}

.red-dot {
  background: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
}

.grey-dot {
  background: #94a3b8;
}

/* Actions Row Buttons */
.row-action-btn {
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  width: 30px !important;
  height: 30px !important;
  transition: all 0.2s ease !important;
}

.edit-btn:hover {
  background: #fffdfa !important;
  border-color: #9a6b27 !important;
  box-shadow: 0 2px 6px rgba(154, 106, 44, 0.15) !important;
}

.delete-btn:hover {
  background: #fef2f2 !important;
  border-color: #ef4444 !important;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.15) !important;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}

/* Modals & Dialogs */
.modal-card {
  background: #ffffff !important;
  border-radius: 14px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.modal-icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fffdf8;
  border: 1px solid #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.modal-close-btn {
  border-radius: 6px !important;
}

.form-input-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #475569;
  margin-bottom: 5px;
}

.required-star {
  color: #ef4444;
}

.form-text-field :deep(.v-input__slot) {
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  min-height: 38px !important;
  font-size: 13px !important;
}

.code-input :deep(input) {
  font-family: monospace;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.field-hint {
  font-size: 11px;
  color: #94a3b8;
  display: block;
  margin-top: 3px;
}

.inline-add-role-btn {
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 700;
  color: #9a6b27;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;
}

.inline-add-role-btn:hover {
  text-decoration: underline;
}

.modal-btn-cancel {
  border-radius: 8px !important;
  text-transform: capitalize !important;
  border-color: #cbd5e1 !important;
}

.modal-btn-primary {
  border-radius: 8px !important;
  text-transform: capitalize !important;
}

/* Confirm Dialog Styles */
.confirm-icon-box {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.warning-box {
  background: #fef3c7;
  border: 1px solid #fde68a;
}

.success-box {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
}

.danger-box {
  background: #fee2e2;
  border: 1px solid #fecaca;
}

.confirm-title {
  font-size: 16.5px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.confirm-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}

.text-muted {
  color: #94a3b8;
}

.text-slate-800 {
  color: #1e293b;
}

.text-slate-900 {
  color: #0f172a;
}
</style>
